// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';

import { Auth2Service } from '@core/auth/auth.service';
import { LoggerService } from '@core/services/logger.service';

/**
 * The in-app roots a Universal Link is allowed to open. An allowlist by design:
 * a link may only land the user on a screen we deliberately expose, never drive
 * the router to an arbitrary path. Matches the web deep-link surface
 * (`/reports?report=…`, `/replay/<mlid>`, `/flights?id=…`).
 */
export const ALLOWED_DEEP_LINK_ROOTS = ['reports', 'replay', 'flights'];

/**
 * Universal Link URL → in-app router path, or null when the link is not one we
 * open. Pure + exported so the mapping is unit-tested without the plugin. Only
 * http(s) Universal Links are honoured (the app has no custom URL scheme for
 * deep-linking — `com.ergossapp.ipwi` is the OAuth callback, not routing).
 * Keeps the path + query verbatim; the target page parses its own query params
 * (e.g. reports reads `?report=&<input>=`, replay reads the `:mlid` segment).
 */
export function toAppPath(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const firstSegment = url.pathname.split('/').filter(Boolean)[0] ?? '';
  if (!ALLOWED_DEEP_LINK_ROOTS.includes(firstSegment)) return null;
  return url.pathname + url.search;
}

/**
 * iOS Universal Links → in-app navigation. Native-iOS only (no-ops elsewhere —
 * `Capacitor.getPlatform()`, matching the push service). Tapping an https link
 * to a report/flight (e.g. an email CTA) opens the app on that screen.
 *
 * Arrival needs infra that is NOT in this repo: an `associated-domains`
 * entitlement (`applinks:<domain>`) AND an apple-app-site-association file
 * hosted at `https://<domain>/.well-known/apple-app-site-association`. Until
 * both exist the listener simply never fires — this service is inert, not broken.
 *
 * Auth timing reuses the app's existing web deep-link stash: if the session is
 * ready we navigate immediately (warm tap); otherwise the target is parked in
 * `redirectAfterLogin`, which app.component consumes once claims arrive
 * (cold launch, before login). Fail-quiet throughout — a bad link must never
 * disturb boot.
 */
@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  private readonly _router = inject(Router);
  private readonly _auth = inject(Auth2Service);
  private readonly _logger = inject(LoggerService);
  private _initialized = false;

  /** Attaches the appUrlOpen listener. Called once at boot (early — before login
   *  — so a cold-launch link is caught and parked before the claims effect runs). */
  async init(): Promise<void> {
    if (this._initialized || Capacitor.getPlatform() !== 'ios') return;
    this._initialized = true;
    try {
      // Lazy import: the plugin's web proxy never loads outside iOS.
      const { App } = await import('@capacitor/app');
      await App.addListener('appUrlOpen', (event) => this.handleUrl(event.url));
      // Cold launch: the opening URL may predate the listener — recover it.
      const launch = await App.getLaunchUrl();
      if (launch?.url) this.handleUrl(launch.url);
    } catch (error) {
      this._logger.error('deeplink', 'Deep link init failed', error);
    }
  }

  /** Maps a link to a route and either navigates (session ready) or parks it in
   *  redirectAfterLogin (cold launch). Public for the spec; fail-quiet. */
  handleUrl(rawUrl: string): void {
    try {
      const target = toAppPath(rawUrl);
      if (!target) return;
      if (this._auth.claimsLoaded() && this._auth.ergsClaims()) {
        void this._router.navigateByUrl(target);
      } else {
        // app.component's claims effect consumes this once login completes.
        localStorage.setItem('redirectAfterLogin', target);
      }
    } catch (error) {
      this._logger.error('deeplink', 'Deep link handling failed', error);
    }
  }
}
